import {
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  map,
  shareReplay,
  startWith,
  type Observable,
} from 'rxjs';
import { action$, extractPayload, ofType, withNamespace } from '@ardoq/rxbeach';
import { globalPaneLoader$ } from 'components/GlobalPaneLoader/globalPaneLoader$';
import { contextPresentation$ } from 'streams/presentations/contextPresentation$';
import { notifyViewLoading } from './actions';
import { getIsViewLoading$ } from './activeViewsStatus/viewStatusReaders';

const ARIA_BUSY_DEBOUNCE_MS = 100;

/** Where the view reports its loading: `notifyViewLoading` until it migrates to `activeViewsStatus$`. */
export type LoadingIndicatorSource = 'notifyViewLoading' | 'activeViewsStatus';

export type LoaderTransitionDuration = '0s' | '2s' | '20ms';

export type LoadingIndicatorViewModel = {
  isLoaderShown: boolean;
  isContentHidden: boolean;
  isAriaBusy: boolean;
  isFromPresentation: boolean;
  transitionDuration: LoaderTransitionDuration;
};

type GetLoadingIndicatorViewModelArgs = {
  viewInstanceId: string;
  loadingSource: LoadingIndicatorSource;
  showContentWhileLoading: boolean;
};

type LoaderInputs = {
  isBusy: boolean;
  isAriaBusy: boolean;
  isGlobalPaneLoaderDisplayed: boolean;
  isFromPresentation: boolean;
};

const toNotifyViewLoadingBusy$ = (
  viewInstanceId: string
): Observable<boolean> =>
  action$.pipe(
    withNamespace(viewInstanceId),
    ofType(notifyViewLoading),
    extractPayload(),
    map(({ isBusy }) => isBusy),
    startWith(true)
  );

const toTransitionDuration = (
  isFromPresentation: boolean,
  isLoaderShown: boolean
): LoaderTransitionDuration => {
  if (isFromPresentation) {
    return '0s';
  }
  return isLoaderShown ? '2s' : '20ms';
};

const toLoadingIndicatorViewModel = (
  showContentWhileLoading: boolean,
  {
    isBusy,
    isAriaBusy,
    isGlobalPaneLoaderDisplayed,
    isFromPresentation,
  }: LoaderInputs
): LoadingIndicatorViewModel => {
  const isLoaderShown = isBusy && !isGlobalPaneLoaderDisplayed;
  return {
    isLoaderShown,
    isContentHidden: !showContentWhileLoading && isLoaderShown,
    isAriaBusy,
    isFromPresentation,
    transitionDuration: toTransitionDuration(isFromPresentation, isLoaderShown),
  };
};

export const getLoadingIndicatorViewModel$ = ({
  viewInstanceId,
  loadingSource,
  showContentWhileLoading,
}: GetLoadingIndicatorViewModelArgs): Observable<LoadingIndicatorViewModel> => {
  const isBusy$ = (
    loadingSource === 'activeViewsStatus'
      ? getIsViewLoading$(viewInstanceId)
      : toNotifyViewLoadingBusy$(viewInstanceId)
  ).pipe(
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  return combineLatest({
    isBusy: isBusy$,
    isAriaBusy: isBusy$.pipe(
      debounceTime(ARIA_BUSY_DEBOUNCE_MS),
      startWith(true),
      distinctUntilChanged()
    ),
    isGlobalPaneLoaderDisplayed: globalPaneLoader$.pipe(
      map(({ isLoading }) => isLoading),
      distinctUntilChanged()
    ),
    isFromPresentation: contextPresentation$.pipe(
      map(Boolean),
      startWith(false),
      distinctUntilChanged()
    ),
  }).pipe(
    map(inputs => toLoadingIndicatorViewModel(showContentWhileLoading, inputs))
  );
};
